import Immutable from "immutable";
import { createAlert } from "actions/alerts";
import { createExpressionAction, trainExpressions } from "actions/expressions";
import { closeModal } from "actions/modal";

import {
  getAllVariables,
  getVariablesPromises,
  deleteResponseVariables
} from "actions/variables";

import { fetchMany, updateOne } from "actions/resources";

import keyConverter from "services/key-converter";
import { getCookie } from "services/cookies";
import { push } from "connected-react-router";
import { toAPI } from "adapters/message";
import { getResource } from "selectors/resources";

export function getResponses(args = {}) {
  const { getVariables, responseId } = args;

  return (dispatch, getState) => {
    /** @type Client */
    const client = getResource(getState(), "client");

    const dispatchCallbacks = getVariables && [
      {
        request: getAllVariables,
        fireOnStatus: "success",
        args: {
          responseId,
          includeInactive: true
        }
      }
    ];

    dispatch({
      CALL_API: {
        method: "get",
        endpoint: "/responses/",
        args: {
          primaryLanguage: client.language
        },
        types: [
          "GET_RESPONSES_REQUEST",
          "GET_RESPONSES_SUCCESS",
          "GET_RESPONSES_FAILURE"
        ],
        dispatchCallbacks
      }
    });
  };
}

/**
 * @param {String} [handle]
 * @param {Object} [options]
 * @param {unknown[]} [options.expressions]
 * @param {unknown} [options.checkedDeep]
 * @param {unknown} [options.search]
 * @param {unknown} [options.logBody]
 * @returns {AsyncAction}
 */
export function createResponse(
  handle = "",
  options = {}
) {
  const {
    expressions = [],
    checkedDeep,
    search,
    logBody
  } = options;

  return (dispatch, getState) => {
    const state = getState();
    const { responses } = state;
    /** @type Client */
    const client = getResource(state, "client");
    dispatch({
      type: "CREATE_RESPONSE",
      handle,
      expressions,
      checkedDeep,
      search,
      responses,
      logBody,
      reviewable: true,
      clientResponses: client.responses,
      language: client.language
    });
  };
}

/**
 * Transform frontend representation of messages to API-compatable messages.
 *
 * @param {Object} messages - Messages to convert
 * @returns {Object} transformed messages
 */
function groomMessages(messages) {
  const apiMessages = toAPI(messages).toJS();

  return keyConverter(apiMessages, "underscore");
}

/**
 * @param {String} responseId
 * @param {String} languageCode
 * @param {String} responseIdToAdd
 * @returns {AsyncAction}
 */
export function addSuggestedResponseAction(responseId, languageCode, responseIdToAdd) {
  return (dispatch, getState) => {
    // Legacy action
    dispatch({
      type: "ADD_SUGGESTED_RESPONSE",
      responseId,
      responseIdToAdd,
      language: languageCode
    });

    const originalResponse = getResource(getState(), "response", responseId);
    dispatch(updateOne("response", responseId, {
      options: Immutable.fromJS({
        ...originalResponse.options.toJS(),
        [languageCode]: [
          ...originalResponse.options.get(languageCode, []),
          {
            target: responseIdToAdd,
            label: ""
          }
        ]
      })
    }));
  };
}

/**
 * Save response
 * @param {Object} response
 * @param {Object} expressionsToSave
 * @param {Object} logBodyToTrain
 * @param {{ parentResponseId: String, parentResponseLanguageCode: String }} options
 */
export function saveResponse(response, expressionsToSave, logBodyToTrain, options = {}) {
  return (dispatch, getState) => {
    let groomedMessages = groomMessages(response.messages);

    const groomedOptions = keyConverter(response.options.toJS(), "underscore");
    const { client } = getState();

    let dispatchCallbacks = [];

    if (options.parentResponseId) {
      dispatchCallbacks = [{
        request(payload) {
          dispatch(addSuggestedResponseAction(
            options.parentResponseId,
            options.parentResponseLanguageCode,
            payload.response.data.response._id
          ));
        },
        fireOnStatus: 201
      }];
    } else {
      dispatchCallbacks = [{
        request: changeToNewPage,
        fireOnStatus: 201
      }];
    }

    if (response.new) {
      // TODO MEAS-221: Remove this action when responses reducer is refactored
      dispatch({
        type: "CREATE_ONE_RESPONSE_REQUEST",
        requestKey: "create-one"
      });

      dispatch({
        CALL_API: {
          method: "post",
          endpoint: "/responses/",
          payload: {
            _id: response.id,
            button_label: response.buttonLabel,
            clarifications: response.clarifications,
            created: response.created,
            handle: response.handle,
            description: response.description,
            messages: groomedMessages,
            new: response.new,
            options: groomedOptions,
            rating: response.rating,
            reserved: response.reserved,
            tags: response.tags,
            updated: response.updated,
            reviewable: response.reviewable,
            live: response.live
          },
          args: {
            oldId: response.id,
            expressionIds: expressionsToSave,
            logBody: logBodyToTrain,
            variables: response.variables,
            client
          },
          types: [
            "CREATE_RESPONSE_REQUEST",
            "CREATE_RESPONSE_SUCCESS",
            "CREATE_RESPONSE_FAILURE"
          ],
          dispatchCallbacks: [
            {
              request: saveNewExpressions,
              fireOnStatus: 201
            },
            {
              request: trainNewMessages,
              fireOnStatus: 201
            },
            {
              request: closeModal,
              fireOnStatus: "success"
            },
            {
              request: createAlert,
              fireOnStatus: 400
            },
            {
              request: createAlert,
              fireOnStatus: "success",
              args: {
                message: `${response.handle} was successfully created.`,
                alertType: "success"
              }
            },
            {
              request: dispatch(fetchMany("responseSideBar",
                getState().responsesPage.query.toJS()))
            },
            {
              request(payload) {
                // TODO MEAS-221: Remove this action when responses reducer is refactored
                dispatch({
                  type: "CREATE_ONE_RESPONSE_SUCCESS",
                  requestKey: "create-one",
                  response: payload.response
                });
              },
              fireOnStatus: "success"
            },
            ...dispatchCallbacks
          ]
        }
      });
    } else {
      getVariablesPromises(response.id, dispatch, getState).then(() => {
        // TODO MEAS-221: Remove this action when responses reducer is refactored
        dispatch({
          type: "SAVE_ONE_RESPONSE_REQUEST",
          requestKey: `save-one-${response.id}`
        });

        /* Fetch the response again because the variable ids
           may have been updated after the CREATE_VARIABLE_SUCCESS action
        */
        const updatedResponse = getState().responsesLoaded.get(response.id);
        groomedMessages = groomMessages(updatedResponse.messages);
        dispatch({
          CALL_API: {
            method: "patch",
            endpoint: `/responses/${updatedResponse.id}`,
            payload: {
              _id: updatedResponse.id,
              button_label: updatedResponse.buttonLabel,
              clarifications: updatedResponse.clarifications,
              created: updatedResponse.created,
              handle: updatedResponse.handle,
              has_forced_quick_replies: updatedResponse.hasForcedQuickReplies,
              description: updatedResponse.description,
              messages: groomedMessages,
              options: groomedOptions,
              rating: updatedResponse.rating,
              reserved: updatedResponse.reserved,
              tags: updatedResponse.tags,
              reviewable: updatedResponse.reviewable,
              updated: updatedResponse.updated,
              live: updatedResponse.live
            },
            args: {
              responseId: updatedResponse.id,
              variables: updatedResponse.variables
            },
            types: [
              "SAVE_RESPONSE_REQUEST",
              "SAVE_RESPONSE_SUCCESS",
              "SAVE_RESPONSE_FAILURE"
            ],
            dispatchCallbacks: [
              {
                request: createAlert,
                fireOnStatus: 400
              },
              {
                request: fetchResponseQuickReplies,
                fireOnStatus: "success"
              },
              {
                request: createAlert,
                fireOnStatus: "success",
                args: {
                  message: `${updatedResponse.handle} was successfully updated.`,
                  alertType: "success"
                }
              },
              {
                request: closeModal,
                fireOnStatus: "success"
              },
              {
                request: dispatch(fetchMany("responseSideBar",
                  getState().responsesPage.query.toJS()))
              },
              {
                request(payload) {
                  // TODO MEAS-221: Remove this action when responses reducer is refactored
                  dispatch({
                    type: "SAVE_ONE_RESPONSE_SUCCESS",
                    requestKey: `save-one-${updatedResponse.id}`,
                    response: payload.response
                  });
                },
                fireOnStatus: "success"
              }
            ]
          }
        });
      });
    }
  };
}

export function updateResponse(responseId, payload) {
  return {
    type: "UPDATE_RESPONSE",
    responseId,
    payload
  };
}

export function reorderResponseMessages(responseId, language, spliceTo, spliceFrom) {
  return {
    type: "REORDER_RESPONSE_MESSAGES",
    responseId,
    spliceTo,
    spliceFrom,
    language
  };
}

/**
 * @param {ResponseRecord} response
 * @param {String} languageCode
 * @param {Number} spliceTo
 * @param {Number} spliceFrom
 * @returns {AsyncAction}
 */
export function reorderSuggestedResponses(response, languageCode, spliceTo, spliceFrom) {
  const responseId = response.id;

  return (dispatch, getState) => {
    // Legacy action
    dispatch({
      type: "REORDER_SUGGESTED_RESPONSES",
      responseId,
      spliceTo,
      spliceFrom,
      language: languageCode
    });

    const originalResponse = getResource(getState(), "response", responseId);

    dispatch(updateOne("response", responseId, {
      options: originalResponse.options.updateIn([languageCode], (selectedResponses) => {
        const movedOption = selectedResponses.getIn([spliceFrom]);

        return selectedResponses
          .splice(spliceFrom, 1)
          .splice(spliceTo, 0, movedOption);
      })
    }));
  };
}

/**
 * @param {Object} p
 * @param {String} p.responseId
 * @param {String} p.language
 * @param {String[]} p.keyPath
 * @param {Number} p.spliceTo
 * @param {Number} p.spliceFrom
 * @returns {AsyncAction}
 */
export function reorderQuickRepliesBlockAction({
  responseId, language, keyPath, spliceTo, spliceFrom
}) {
  return (dispatch) => {
    // Legacy action
    dispatch({
      type: "REORDER_QUICK_REPLIES_BLOCK",
      responseId,
      spliceTo,
      spliceFrom,
      language,
      keyPath
    });
  };
}

/**
 * Create a new `message` of `messageType` at `ketPath`
 * @param {Object} p
 * @param {String} p.responseId
 * @param {String} p.messageType
 * @param {String} p.language
 * @param {Number} p.index
 * @param {String[]} [p.keyPath]
 * @param {string} p.messageDisplayType
 * @returns {Object}
 */
export function createMessage({
  responseId, messageType, messageDisplayType, language, index, keyPath
}) {
  return {
    type: "CREATE_RESPONSE_MESSAGE",
    responseId,
    messageType,
    messageDisplayType,
    language,
    index,
    keyPath
  };
}

/**
 * @param {Object} param
 * @param {String} param.responseId
 * @param {String} param.messageId
 * @param {String} param.messageType
 * @param {String} param.language
 * @param {Number} param.index
 * @param {Boolean} param.droppable
 * @param {string} param.messageDisplayType
 * @returns {Object}
 */
export function createNestedMessage({
  responseId, messageId, messageType, messageDisplayType, language, index, droppable
}) {
  return {
    type: "CREATE_NESTED_RESPONSE_MESSAGE",
    responseId,
    messageId,
    messageType,
    messageDisplayType,
    language,
    index,
    droppable
  };
}

/**
 * moveResponseMessageToNestedMessage: for when a user would like
 * to move a message into a nested message
 *
 * @param {Object} p
 * @param {String} p.responseId
 * @param {String} p.messageToMoveId
 * @param {String} p.language
 * @param {Number} p.fromIndex
 * @param {Number} p.toNestedIndex
 * @param {String} p.destinationNestedMessageType
 * @param {String} p.destinationMessageId
 *
 * @returns {Object}
 */
export function moveResponseMessageToNestedMessage({
  responseId,
  messageToMoveId,
  language,
  fromIndex,
  toNestedIndex,
  destinationNestedMessageType,
  destinationMessageId
}) {
  return {
    type: "MOVE_RESPONSE_MESSAGE_TO_NESTED_MESSAGE",
    responseId,
    messageToMoveId,
    language,
    fromIndex,
    toNestedIndex,
    destinationNestedMessageType,
    destinationMessageId
  };
}

/**
 * moveNestedMessageToResponseMessage: for when we want to move a
 * message out of a nested message.
 *
 * @param {String} responseId
 * @param {Object} options
 * @param {String} options.messageToMoveId
 * @param {String} options.language
 * @param {Number} options.fromNestedIndex
 * @param {Number} options.toIndex
 * @param {String} options.sourceNestedMessageType
 * @param {String} options.sourceMessageId
 *
 * @returns {Object}
 */
export function moveNestedMessageToResponseMessage(responseId, options) {
  const {
    messageToMoveId,
    language,
    fromNestedIndex,
    toIndex,
    sourceNestedMessageType,
    sourceMessageId
  } = options;

  return {
    type: "MOVE_NESTED_MESSAGE_TO_RESPONSE_MESSAGE",
    responseId,
    messageToMoveId,
    language,
    fromNestedIndex,
    toIndex,
    sourceNestedMessageType,
    sourceMessageId
  };
}

/**
 * @param {Object} p
 * @param {String} p.responseId
 * @param {String} p.language
 * @param {String} p.messageId
 * @param {String} p.messagesKey
 * @param {unknown} p.spliceTo
 * @param {unknown} p.spliceFrom
 * @returns {Object}
 */
export function reorderNestedMessages({
  responseId,
  language,
  messageId,
  messagesKey,
  spliceTo,
  spliceFrom
}) {
  return {
    type: "REORDER_NESTED_RESPONSE_MESSAGES",
    responseId,
    messageId,
    messagesKey,
    spliceTo,
    spliceFrom,
    language
  };
}

export function addButtonLabelLanguage(language, responseId) {
  return {
    type: "ADD_BUTTON_LABEL_LANGUAGE_TO_RESPONSE",
    language,
    responseId
  };
}

export function deleteButtonLabelLanguage(languageCode, responseId) {
  return {
    type: "DELETE_RESPONSE_BUTTON_LANGUAGE",
    responseId,
    languageCode
  };
}

export function deleteResponse(response) {
  return (dispatch, getState) => {
    const responseId = response.id;

    deleteResponseVariables(getState, dispatch, responseId).then(() => {
      // TODO MEAS-221: Remove this action when responses reducer is refactored
      dispatch({
        type: "DELETE_ONE_RESPONSE_REQUEST",
        requestKey: `delete-one-${response.id}`
      });

      dispatch({
        CALL_API: {
          method: "delete",
          endpoint: `/responses/${response.id}`,
          args: {
            responseId: response.id
          },
          types: [
            "DELETE_RESPONSE_REQUEST",
            "DELETE_RESPONSE_SUCCESS",
            "DELETE_RESPONSE_FAILURE"
          ],
          dispatchCallbacks: [
            {
              request: createAlert,
              fireOnStatus: "success",
              args: {
                message: `${response.handle} was successfully deleted.`,
                alertType: "success"
              }
            },
            {
              request: dispatch(fetchMany("responseSideBar",
                getState().responsesPage.query.toJS()))
            },
            {
              request() {
                // TODO MEAS-221: Remove this action when responses reducer is refactored
                dispatch({
                  type: "DELETE_ONE_RESPONSE_SUCCESS",
                  requestKey: `delete-one-${response.id}`,
                  id: response.id
                });
              },
              fireOnStatus: "success"
            }
          ]
        }
      });
    });
  };
}


function saveNewExpressions(response) {
  if (!response.expressionIds.size) {
    return null;
  }

  return (dispatch) => {
    dispatch(
      trainExpressions(
        response.expressionIds.toJS(),
        null,
        response.response.data.response._id
      )
    );
  };
}

function trainNewMessages(response) {
  if (!response.logBody) {
    return null;
  }

  return (dispatch) => {
    dispatch(
      createExpressionAction(response.response.data.response._id, response.logBody)
    );
  };
}

function changeToNewPage(response) {
  return (dispatch) => {
    dispatch(push(`/answers/${response.response.data.response._id}`));
  };
}

/**
 * Similar to saveResponse, but updates the live field on the response
 * without updating the rest of the response fields
 * @param {Object} response Response record
 * @param {Boolean} responseStatus Live true or false
 * @returns {Object}
 */
export function toggleResponseStatus(response, responseStatus) {
  const responseId = response.id;

  return {
    CALL_API: {
      method: "patch",
      endpoint: `/responses/${responseId}`,
      payload: {
        live: responseStatus
      },
      args: {
        responseId,
        responseStatus
      },
      types: [
        "UPDATE_RESPONSE_STATUS_REQUEST",
        "UPDATE_RESPONSE_STATUS_SUCCESS",
        "UPDATE_RESPONSE_STATUS_FAILURE"
      ],
      dispatchCallbacks: [
        {
          request: createAlert,
          fireOnStatus: "error"
        }
      ]
    }
  };
}

/**
 * Gets the Answer references and dispatches callbacks that conditionally handle the API response
 * @param {Object} response Response record
 * @param {String} action "live" or "delete"
 * @returns {Object}
 */
export function getResponseReferencesBeforeAction(response, action = "live") {
  return {
    CALL_API: {
      method: "get",
      endpoint: `/responses/references/${response.id}`,
      args: {
        responseSnapshot: response,
        action
      },
      types: [
        "GET_RESPONSE_REFERENCE_REQUEST",
        "GET_RESPONSE_REFERENCE_SUCCESS",
        "GET_RESPONSE_REFERENCE_FAILURE"
      ],
      dispatchCallbacks: [
        {
          request: handleResponseReferenceSuccess,
          fireOnStatus: "success"
        },
        {
          request: handleResponseReferenceSuccess,
          fireOnStatus: 400
        }
      ]
    }
  };
}

/**
 * Handles actions toggling live status and deleting an Answer
 * @param {Object} args Contains response, responseSnapshot, and action
 * @returns {Function}
 */
function handleResponseReferenceSuccess(args) {
  const { response, responseSnapshot, action } = args;

  const responseData = keyConverter(response.data);
  const {
    responseFallbackReferences,
    responseQuickReplyReferences,
    responseAnswersRedirectReferences
  } = responseData;

  /*
    Used to convert responseFallbackReferences and responseQuickReplyReferences
    to a more usable structure
   */
  const responseDataToObject = (array = []) => (
    array.reduce((obj, item) => ({
      ...obj,
      [item.response.id]: item
    }), {})
  );

  const setModal = (caseProps) => {
    const baseProps = {
      action,
      responseSnapshot,
      responseFallbackReferences: (
        responseFallbackReferences &&
        responseDataToObject(responseFallbackReferences)
      ),
      responseQuickReplyReferences: (
        responseQuickReplyReferences &&
        responseDataToObject(responseQuickReplyReferences)
      ),
      responseAnswersRedirectReferences: (
        responseAnswersRedirectReferences &&
        responseDataToObject(responseAnswersRedirectReferences)
      )
    };
    const modalProps = { ...baseProps, ...caseProps };

    return {
      type: "SET_MODAL",
      payload: {
        isOpen: true,
        view: "MODAL_WARNING",
        modalProps
      }
    };
  };

  if (responseFallbackReferences) {
    const caseProps = {
      title: "Answer Used Elsewhere",
      message: `This Answer is used as a fall-back in the following Answers and can’t be ${action === "delete" ? "deleted" : "turned off"} until this is changed.`,
      buttons: [
        "cancel"
      ],
      type: "fallback",
      totalReferenceButtons: Object.keys(responseFallbackReferences).length
    };

    return setModal(caseProps);
  }

  if (responseAnswersRedirectReferences) {
    const caseProps = {
      title: "Answer Used Elsewhere",
      message: `This Answer is used as a fall-back in the following Answers and can’t be ${action === "delete" ? "deleted" : "turned off"} until this is changed.`,
      buttons: [
        "cancel"
      ],
      type: "fallback",
      totalReferenceButtons: Object.keys(responseAnswersRedirectReferences).length
    };

    return setModal(caseProps);
  }


  if (responseQuickReplyReferences && Object.keys(responseQuickReplyReferences).length > 0) {
    const caseProps = {
      title: "Answer Used Elsewhere",
      message: `This Answer is used as a quick reply in other Answers. ${action === "delete" ? "Delete it" : "Hide it"} anyway?`,
      buttons: [
        action === "delete" ? "delete" : "turn-off",
        "cancel"
      ],
      type: "quickReply",
      totalReferenceButtons: Object.keys(responseQuickReplyReferences).length
    };

    return setModal(caseProps);
  }

  if (action === "delete") {
    const caseProps = {
      title: "Delete Answer",
      message: "Are you sure you would like to delete this Answer?",
      buttons: [
        "delete",
        "cancel"
      ],
      type: "delete"
    };

    return setModal(caseProps);
  }

  return handleLiveToggle(
    responseSnapshot,
    action
  );
}

/**
 * Handle updating live state or showing ModalWarning
 * @param {Object} responseSnapshot Response record
 * @param {String} action "live" or "delete"
 * @returns {Function}
 */
export function handleLiveToggle(responseSnapshot, action) {
  const toggleAction = responseSnapshot.live ? "off" : "on";

  if (responseSnapshot.isDirty && !responseSnapshot.live) {
    const rememberUnsavedChoice = getCookie("rememberUnsavedChoice");
    let caseProps;

    if (!responseSnapshot.isValid) {
      caseProps = {
        title: "Unsaved Changes",
        message: "You have invalid unsaved changes in your Answer. What would you like to do?",
        buttons: [
          `turn-${toggleAction}-changes`,
          "cancel"
        ],
        type: "invalidUnsaved"
      };
    } else if (!rememberUnsavedChoice) {
      caseProps = {
        title: "Unsaved Changes",
        message: "You have unsaved changes in your Answer. What would you like to do?",
        buttons: [
          `save-turn-${toggleAction}`,
          `turn-${toggleAction}-changes`
        ],
        type: "unsaved"
      };
    } else {
      return (dispatch) => {
        if (rememberUnsavedChoice === "save") {
          dispatch(updateResponse(responseSnapshot.id, { live: !responseSnapshot.live }));
          dispatch(saveResponse(responseSnapshot));
        } else {
          dispatch(toggleResponseStatus(
            responseSnapshot,
            !responseSnapshot.get("live")
          ));
        }
      };
    }

    const modalProps = {
      action,
      responseSnapshot,
      ...caseProps
    };

    return {
      type: "SET_MODAL",
      payload: {
        isOpen: true,
        view: "MODAL_WARNING",
        modalProps
      }
    };
  }

  return (dispatch) => {
    dispatch(toggleResponseStatus(
      responseSnapshot,
      !responseSnapshot.get("live")
    ));
  };
}

/**
 * Load a single response from API
 * @param {Object} args - {responseId}
 */
export function getResponseById(args) {
  return (dispatch, getState) => {
    const client = getResource(getState(), "client");
    const { responsesLoaded } = getState();
    let { responseId } = args;

    if (!responseId && args.type === "GET_RESPONSES_SHALLOW_SUCCESS") {
      responseId = args.response.data.responses[0]._id;
    }

    // If we've already loaded the response, don't load it again.
    if (responsesLoaded.get(responseId)) {
      return;
    }

    // TODO MEAS-221: Remove this action when responses reducer is refactored
    dispatch({
      type: "FETCH_ONE_RESPONSE_REQUEST",
      requestKey: responseId
    });

    dispatch({
      CALL_API: {
        method: "get",
        endpoint: `/responses/${responseId}`,
        args: {
          primaryLanguage: client.language
        },
        types: [
          "GET_RESPONSES_BY_ID_REQUEST",
          "GET_RESPONSES_BY_ID_SUCCESS",
          "GET_RESPONSES_BY_ID_FAILURE"
        ],
        dispatchCallbacks: [
          {
            request(payload) {
              // TODO MEAS-221: Remove this action when responses reducer is refactored
              dispatch({
                type: "FETCH_ONE_RESPONSE_SUCCESS",
                requestKey: responseId,
                response: payload.response
              });
            },
            fireOnStatus: "success"
          }
        ]
      }
    });
  };
}

/**
 * An action to fetch all of our shallow responses
 * @param {Object} args
 * @returns {Function}
 */
export function fetchAllResponsesShallow(args = {}) {
  return (dispatch, getState) => {
    /** @type Client */
    const client = getResource(getState(), "client");
    const { getVariables } = args;

    const dispatchCallbacks = [
      {
        request: getResponseById,
        fireOnStatus: "success",
        args
      }
    ];

    if (getVariables) {
      dispatchCallbacks.push(
        {
          request: getAllVariables,
          fireOnStatus: "success",
          args: {
            includeInactive: true,
            ...args
          }
        }
      );
    }

    dispatch({
      CALL_API: {
        method: "get",
        endpoint: "/responses/shallow",
        args: {
          primaryLanguage: client.language
        },
        types: [
          "GET_RESPONSES_SHALLOW_REQUEST",
          "GET_RESPONSES_SHALLOW_SUCCESS",
          "GET_RESPONSES_SHALLOW_FAILURE"
        ],
        dispatchCallbacks
      }
    });
  };
}

/**
 *
 * @param {String} responseId
 * @param {Number} index
 * @param {String} language
 * @param {List<MessageRecord>} messages
 */
export function pasteResponse(responseId, index, language, messages) {
  return {
    type: "PASTE_RESPONSE_MESSAGE",
    responseId,
    index,
    language,
    messages
  };
}

/**
 * fetchResponseQuickReplies
 * An action to fetch the quick replies for a particular response. This action
 * should really only be used for rendering an answer flow.
 *
 * The response from API will look something like this:
 *
 * {
 *  id: <response_id>,
 *  options: {
 *    [lang_code]: [
 *      {
 *        [qr_response_id],
 *        [handle]
 *      }
 *      ...
 *    ]
 *    ...
 *  }
 *
 * @param {Object.<String>} args
 * @returns {Object}
 */
export function fetchResponseQuickReplies({ responseId }) {
  return {
    CALL_API: {
      method: "get",
      endpoint: `/responses/${responseId}/quickreplies`,
      args: {
        responseId
      },
      types: [
        "GET_RESPONSE_QUICK_REPLIES_REQUEST",
        "GET_RESPONSE_QUICK_REPLIES_SUCCESS",
        "GET_RESPONSE_QUICK_REPLIES_FAILURE"
      ]
    }
  };
}
